/**
 * Contrat d'erreur du backend (S5.4a, audit 02 reco 3).
 *
 * Sur les routes du back-office, les erreurs rendues par le handler et les refus des contrôleurs
 * convertis rendent `{ code, message }` : `code` est le motif stable, `message` un texte
 * affichable tel quel (quelques messages hérités restent en anglais). Un 422 VineJS y ajoute
 * `errors`, la liste par champ que les formulaires lisent déjà. Les refus écrits à la main dans
 * les contrôleurs pas encore convertis rendent encore `{ error }`. Les routes des clients machine
 * (CPI, Checkpoint, Luna), de l'app et de store-app gardent leur corps d'erreur hérité.
 *
 * Ré-exporté par `src/index.ts` ; entrée directe `@gozoki/api-types/api-errors.js`.
 */

/**
 * Codes génériques, tirés du statut HTTP. Un refus métier peut porter un code plus précis
 * (`PERIOD_TOO_LONG`) ; un refus RBAC garde `E_RBAC_PERMISSION_DENIED`, que le back-office lit pour
 * afficher les permissions manquantes (`missingPermissions`).
 */
export const API_ERROR_CODES = {
  badRequest: 'bad_request',
  unauthorized: 'unauthorized',
  forbidden: 'forbidden',
  notFound: 'not_found',
  conflict: 'conflict',
  payloadTooLarge: 'payload_too_large',
  validation: 'validation',
  tooManyRequests: 'too_many_requests',
  internal: 'internal',
  upstreamUnavailable: 'upstream_unavailable',
  serviceUnavailable: 'service_unavailable',
} as const

export type GenericApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES]

/** Erreur d'un champ dans un 422 VineJS. */
export interface ApiFieldValidationError {
  field: string
  message: string
  rule: string
  meta?: Record<string, unknown>
}

/** Corps d'erreur des routes du back-office. */
export interface ApiErrorBody {
  /** Motif stable : un code générique (`API_ERROR_CODES`) ou un code métier de la route. */
  code: string
  /** Message en français, affichable tel quel. En production, celui d'un 5xx ne dit rien de la cause. */
  message: string
  /** 422 VineJS : erreurs par champ, la première reprise dans `message`. */
  errors?: ApiFieldValidationError[]
}
